/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-tagless-components */
// Ember imports
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
// External imports
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { mapBy } from '@linc-technologies/linced-common/utils/arrays';
import { classNames, tagName } from '@ember-decorators/component';
@tagName('span')
@classNames('md-button', 'hero-notifications')
export default class HeroNotifications extends Component {
  // Component versioning
  @tracked visualStyle = 'v1';

  htmlSafe = htmlSafe;
  Scopes = Scopes;

  // Services
  @service
  notifications;

  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Attributes
  // isDialogOpen indicates whether the notifications dialog is open
  @tracked
  isDialogOpen = false;

  // Number of notifications to fetch/show.
  // Max 10 for post notifications and 5 for other notifications
  @tracked numToShow = 15;

  // markAllReadInProgress provides a loading state for the async button.
  @tracked
  markAllReadInProgress = false;

  // List of unread notifications.
  @alias('notifications.unreadPostNotifications')
  unreadPostNotifications;

  @alias('notifications.unreadOtherNotifications')
  unreadOtherNotifications;

  @alias('notifications.numUnreadPost')
  numUnreadPost;

  @alias('notifications.numUnreadOther')
  numUnreadOther;

  @alias('notifications.totalUnread')
  totalUnread;

  // Whether we're currently marking notifications as read.
  @alias('notifications.markReadInProgress')
  markReadInProgress;

  // Whether we're currently refreshing notifications from the server.
  @alias('notifications.isRefreshing')
  isRefreshing;

  @computed('totalUnread', 'numToShow')
  get unreadLabel() {
    if (this.totalUnread >= this.numToShow) {
      return `${this.numToShow}+`;
    }
    return this.totalUnread;
  }

  @computed('totalUnread')
  get icon() {
    if (this.totalUnread === 0) {
      return 'notifications_none';
    }
    return 'notifications';
  }

  @computed('session.scopes.[]')
  get isCaregiver() {
    return this.permissions.equal(Scopes.CAREGIVER);
  }

  @computed('unreadOtherNotifications.[]', 'unreadPostNotifications.[]')
  get combinedUnreadNotifications() {
    return this.combineNotifications();
  }

  combineNotifications() {
    let tempNotifications = [
      ...this.unreadOtherNotifications,
      ...this.unreadPostNotifications,
    ];

    // Sort the tempNotifications array by date
    tempNotifications.sort((a, b) => {
      let aDate = this.getDateFromNotification(a);
      let bDate = this.getDateFromNotification(b);
      return bDate - aDate;
    });

    return tempNotifications;
  }

  getDateFromNotification(notification) {
    return notification.isBookingOrEvent
      ? notification.date
      : notification.entity.date;
  }

  @action
  openDialog() {
    this.refresh();
    this.isDialogOpen = true;
  }

  @action
  closeDialog() {
    this.isDialogOpen = false;
  }

  @action
  refresh() {
    this.notifications.refresh();
  }

  @action
  markTheseRead() {
    if (!isEmpty(this.unreadPostNotifications)) {
      let entityIds = mapBy(this.unreadPostNotifications, 'entityId');
      this.notifications.markEntitiesRead(entityIds);
    }
    if (!isEmpty(this.unreadOtherNotifications)) {
      let resultIds = mapBy(this.unreadOtherNotifications, 'resultId');
      this.notifications.markResultsRead(resultIds);
    }
  }

  @action
  markAllRead() {
    this.markAllReadInProgress = true;
    this.notifications
      .markAllRead()
      .then(() => {
        this.toaster.success(`All notifications marked read`);
      })
      .catch(() => {
        this.toaster.error(
          "Notifications couldn't be marked as read. Please try again later.",
        );
      })
      .finally(() => {
        this.notifications.refresh();
        this.markAllReadInProgress = false;
      });
  }
}
